import { useTranslation } from "@redotech/redo-i18n";
import {
  RedoCheckbox,
  type CheckboxValue,
} from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoRadioGroup } from "@redotech/redo-web/arbiter-components/radio/redo-radio-group";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import {
  type RedoCarrierAccountsChoice,
  type RedoCarriersAllSelection,
  type ReturnRateSourceOption,
} from "../rate-source-selection-utils";
import { getAllSpecificNoneRadioOptions } from "./all-specific-none-radio-options";
import { SpecificRateSourceCheckbox } from "./specific-rate-source-checkbox";

export interface RedoCarrierAccountsSectionProps {
  choice: RedoCarrierAccountsChoice;
  options: ReturnRateSourceOption[];
  selectedOptionIds: Set<ReturnRateSourceOption["id"]>;
  allSelection: RedoCarriersAllSelection;
  onChoiceChange(choice: RedoCarrierAccountsChoice): void;
  onOptionChange(option: ReturnRateSourceOption, selected: boolean): void;
  onAllChange(selected: boolean): void;
}

export function RedoCarrierAccountsSection({
  choice,
  options,
  selectedOptionIds,
  allSelection,
  onChoiceChange,
  onOptionChange,
  onAllChange,
}: RedoCarrierAccountsSectionProps) {
  const { t } = useTranslation();

  return (
    <Flex dir="column" gap="sm">
      <Text fontSize="xs" textColor="secondary">
        {t("{{brandName}} carrier accounts", { brandName: "Redo" })}
      </Text>
      <RedoRadioGroup<RedoCarrierAccountsChoice>
        gap="sm"
        options={getAllSpecificNoneRadioOptions({
          allLabel: t("All"),
          noneLabel: t("None"),
          specificLabel: t("Specific carriers"),
          specificChildren: (
            <SpecificRedoCarrierAccountOptions
              allSelection={allSelection}
              onAllChange={onAllChange}
              onOptionChange={onOptionChange}
              options={options}
              selectedOptionIds={selectedOptionIds}
            />
          ),
        })}
        setValue={onChoiceChange}
        size="xs"
        value={choice}
      />
    </Flex>
  );
}

function SpecificRedoCarrierAccountOptions({
  options,
  selectedOptionIds,
  allSelection,
  onOptionChange,
  onAllChange,
}: Omit<RedoCarrierAccountsSectionProps, "choice" | "onChoiceChange">) {
  const { t } = useTranslation();
  const allCheckboxValue: CheckboxValue =
    allSelection === "all"
      ? true
      : allSelection === "some"
        ? "indeterminate"
        : false;

  return (
    <Flex dir="column" gap="sm" pl="xl">
      <RedoCheckbox
        label={t("All carriers")}
        setValue={() => {
          // Indeterminate -> select all, matching the accordion's cycle.
          onAllChange(allSelection !== "all");
        }}
        size="xs"
        value={allCheckboxValue}
      />
      {options.map((option) => (
        <SpecificRateSourceCheckbox
          key={option.id}
          onChange={(selected) => {
            onOptionChange(option, selected);
          }}
          option={option}
          selected={selectedOptionIds.has(option.id)}
        />
      ))}
    </Flex>
  );
}
